import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ButtonFilled from "../../../../components/FormElements/ButtonFilled";
import Modal from "../../../../portals/Modal";
import TextField from "../../../../components/FormElements/TextField";
import { useBonusReferral } from "../../hooks/useBonusReferral";
import PageHeader from "../../../../components/PageHeader";
import Checkbox from "../../../../components/FormElements/Checkbox";
import CustomSelect from "../../../../components/FormElements/Select";
import styles from './styles.module.scss';
import VideoModal from "../../../../components/VideoModal";
import YOUTUBE_LINKS from "../../../../config/youtubeLinks";
import CascadeInfoBanner from "../../../../components/CascadeInfoBanner";
import branchApi from "../../../../store/branchStore/branchApi";

const SUPPORT_WHATSAPP = "https://wa.me/77779503083";

export default function ProfileBonusReferral() {
    const { t } = useTranslation();
    const {
        formik,
        errorModal,
        setErrorModal,
        pending,
        receivers,
        setReceivers,
        isAccessEnabled,
    } = useBonusReferral(t);

    const info = useSelector((s) => s.branchReducer.info);
    const [cascadeReferralExpiry, setCascadeReferralExpiry] = useState(null);

    useEffect(() => {
        if (info?.configuration?.private_cashback?.is_cascade) {
            branchApi.getBalanceExpiration(info.id)
                .then(res => {
                    if (res?.data?.referral !== undefined) {
                        setCascadeReferralExpiry(res.data.referral);
                    }
                })
                .catch(() => {});
        }
    }, [info]);

    // useMemo must be called before any conditional returns to follow Rules of Hooks
    const expireIntervalOptions = useMemo(() => ([
        { label: t('withoutTerm'), value: 0 },
        { label: t('month'), value: 2.628e+9 },
        { label: t('twoWeeks'), value: 1.2096e+9 },
        { label: t('two'), value: 2*2.628e+9 },
        { label: t('three'), value: 3*2.628e+9 },
        { label: t('halfYear'), value: 6*2.628e+9 },
        { label: t('year'), value: 3.154e+10 },
        { label: t('oneAndHalfYear'), value: 1.5*3.154e+10 },
        { label: t('twoYears'), value: 2*3.154e+10 },
    ]), [t]);

    // If access is not enabled, show forbidden banner
    if (!isAccessEnabled) {
        return (
            <>
                <PageHeader title={t('Referral.system')}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
                        <VideoModal url={YOUTUBE_LINKS.BonusReferral} />
                    </div>
                </PageHeader>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 24px',
                    textAlign: 'center',
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    margin: '24px',
                    minHeight: '300px'
                }}>
                    <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        backgroundColor: '#fee2e2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '24px'
                    }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#ef4444" strokeWidth="2"/>
                            <path d="M15 9L9 15M9 9L15 15" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>

                    <h2 style={{
                        fontSize: '24px',
                        fontWeight: '600',
                        color: '#1D1F23',
                        marginBottom: '12px',
                        margin: '0 0 12px 0'
                    }}>
                        Функция недоступна
                    </h2>

                    <p style={{
                        fontSize: '16px',
                        color: '#6b7280',
                        marginBottom: '8px',
                        margin: '0 0 8px 0'
                    }}>
                        Реферальная система не активирована для вашего аккаунта.
                    </p>

                    <p style={{
                        fontSize: '14px',
                        color: '#9ca3af',
                        marginBottom: '32px',
                        margin: '0 0 32px 0'
                    }}>
                        Для подключения данной функции, пожалуйста, свяжитесь с нашей службой поддержки.
                    </p>

                    <a
                        href={SUPPORT_WHATSAPP}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                    >
                        <ButtonFilled variant="green">
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                                Написать в WhatsApp
                            </span>
                        </ButtonFilled>
                    </a>
                </div>
            </>
        );
    }

  return (
      <>
          <PageHeader title={t('Referral.system')}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
              <VideoModal url={YOUTUBE_LINKS.BonusReferral} />
            </div>
          </PageHeader>

          {info?.configuration?.private_cashback?.is_cascade && (
              <div style={{ maxWidth: 600, marginBottom: 8, marginLeft: 24 }}>
                  <CascadeInfoBanner
                      sourceLabel={t('NSBonusMode.sourceReferral')}
                      expirationValue={cascadeReferralExpiry}
                  />
              </div>
          )}

          <div className={styles.container}>
              <form onSubmit={formik.handleSubmit}>
                  {/* Reward type */}
                  <div className={styles.block}>
                      <p>{t("Referral.type")}</p>
                      <div style={{ display: "flex", gap: "16px" }}>
                          <Checkbox
                            shape="round"
                            label={t("Referral.fixBonus")}
                            checked={formik.values.type === 'FIXED'}
                            onChange={() => {
                                formik.setFieldValue('type', 'FIXED');
                                formik.setFieldValue("referee_fixed_amount", 0);
                                formik.setFieldValue("referee_percentage", 0);
                                formik.setFieldValue("referrer_fixed_amount", 0);
                                formik.setFieldValue("referrer_percentage", 0);
                            }}
                          />
                          <Checkbox
                            shape="round"
                            label={t("Referral.precentBonus")}
                            checked={formik.values.type === 'PERCENTAGE'}
                            onChange={() => {
                                formik.setFieldValue('type', 'PERCENTAGE');
                                formik.setFieldValue("referee_fixed_amount", 0);
                                formik.setFieldValue("referee_percentage", 0);
                                formik.setFieldValue("referrer_fixed_amount", 0);
                                formik.setFieldValue("referrer_percentage", 0);
                            }}
                          />
                      </div>
                      <p className={styles['block__text']} style={{ marginTop: 8 }}>
                        {formik.values.type === 'FIXED' ? t('Referral.typeHintFixed') : t('Referral.typeHintPercentage')}
                      </p>
                  </div>
                  {/* Expire interval */}
                  <div className={styles.block} style={{ maxWidth: '420px' }}>
                      <p>{t('Referral.expireInterval')}</p>
                      <CustomSelect
                        options={expireIntervalOptions}
                        value={formik.values.expire_interval}
                        onSelect={(opt) => formik.setFieldValue('expire_interval', opt)}
                      />
                  </div>
                  <div className={styles.block}>
                      <p>{t("Referral.receiverBonusQuestion")}</p>
                      <div style={{ display: "flex", flexDirection: 'column', gap: "16px" }}>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                              <Checkbox
                                label={t("Referral.client")}
                                checked={receivers.includes('CLIENT')}
                                onChange={() => setReceivers(receivers.includes('CLIENT') ? receivers.filter(r => r !== 'CLIENT') : [...receivers, 'CLIENT'])}
                              />
                              <p className={styles['block__text']} style={{ margin: 0, textTransform: 'lowercase' }}>{t('Referral.clientInfo')}</p>
                          </div>

                          <Checkbox
                            checked={receivers.includes('FRIEND')}
                            onChange={() => setReceivers(receivers.includes('FRIEND') ? receivers.filter(r => r !== 'FRIEND') : [...receivers, 'FRIEND'])}
                            label={t("Referral.friend")}
                          />
                      </div>
                  </div>
                  {receivers.length ? (
                    <div className={styles.block}>
                        <p>{t("Referral.bonusSum")}</p>
                        <div style={{ display: "flex", flexDirection: 'column', maxWidth: '300px', gap: "16px" }}>
                            {receivers.includes('CLIENT') && (
                              <TextField
                                label={t('Referral.bonusToInviter')}
                                placeholder={formik.values.type === 'FIXED' ? '8' : '8%'}
                                labelInline
                                disabled={pending}
                                required
                                type="number"
                                value={formik.values.type === 'FIXED' ? formik.values.referrer_fixed_amount || '' : formik.values.referrer_percentage || ''}
                                onChange={(e) => isFinite(e.target.value) && formik.setFieldValue(formik.values.type === 'FIXED' ? 'referrer_fixed_amount' : 'referrer_percentage', e.target.value)}
                              />
                            )}
                            {receivers.includes('FRIEND') && (
                              <TextField
                                label={t('Referral.bonusToInvitee')}
                                placeholder={formik.values.type === 'FIXED' ? '8' : '8%'}
                                labelInline
                                disabled={pending}
                                required
                                type="number"
                                value={formik.values.type === 'FIXED' ? formik.values.referee_fixed_amount || '' : formik.values.referee_percentage || ''}
                                onChange={(e) => isFinite(e.target.value) && formik.setFieldValue(formik.values.type === 'FIXED' ? 'referee_fixed_amount' : 'referee_percentage', e.target.value)}
                              />
                            )}
                            <p className={styles['block__text']} style={{ margin: 0, whiteSpace: 'nowrap' }}>{t('Referral.bonusAfterPurchase')}</p>
                        </div>
                    </div>
                  ) : null}
                  {/* Transactions limit */}
                  <div className={styles.block} style={{ maxWidth: '500px' }}>
                       <TextField
                        label={t('Referral.transactionsLimit')}
                        placeholder="3"
                        labelInline
                        disabled={pending}
                        type="number"
                        value={formik.values.transactions_limit || ''}
                        onChange={(e) => isFinite(e.target.value) && formik.setFieldValue('transactions_limit', e.target.value)}
                       />
                       <p className={styles['block__text']}>{t('Referral.transactionsLimitHint')}</p>
                  </div>
                  {/* Minimum purchase amount */}
                  <div className={styles.block} style={{ maxWidth: '500px' }}>
                      <TextField
                        label={t('Referral.minPurchaseAmount')}
                        placeholder="0"
                        labelInline
                        disabled={pending}
                        type="number"
                        value={formik.values.min_purchase_amount || ''}
                        onChange={(e) => isFinite(e.target.value) && formik.setFieldValue('min_purchase_amount', e.target.value)}
                      />
                      <p className={styles['block__text']}>{t('Referral.minPurchaseAmountHint')}</p>
                  </div>

                  <div className={styles.block} style={{ maxWidth: '720px' }}>
                      <p>{t('Referral.inviteText')}</p>
                      <textarea
                        value={formik.values.invite_text || ''}
                        onChange={(e) => formik.setFieldValue('invite_text', e.target.value)}
                        placeholder={t('Referral.inviteTextPlaceholder') || ''}
                        rows={4}
                        style={{ width: '100%' }}
                        disabled={pending}
                      />
                      <p className={styles['block__text']}>{t('Referral.inviteTextHint')}</p>
                  </div>

                  <div className={styles.footer}>
                      <ButtonFilled type="submit" loading={pending}>{t('save')}</ButtonFilled>
                  </div>
              </form>
          </div>


          <Modal
            open={errorModal} size="small"
            onClickClose={() => setErrorModal(false)}>
              <div className={styles.modal}>
                  <p>
                      {t('optionForbidden')} <br/>
                      {t('touchModerator')}
                  </p>
                  <div>
                      <ButtonFilled
                        onClick={() => setErrorModal(false)}
                      >{t('close')}</ButtonFilled>
                  </div>
              </div>
          </Modal>
      </>
    )
}
